// Content Security Policy domains
export const CSP_DOMAINS = {
  defaultSrc: [
    'https://*.concern.net',
    'https://*.concern.org.uk',
    'https://*.systemseed.host',
    'https://*.google.com',
    'https://*.googleapis.com',
    'https://*.googletagmanager.com',
    'https://*.gstatic.com',
    'https://*.cookiebot.com',
    'https://*.stripe.com',
    'https://*.sociablekit.com',
    'https://*.rollbar.com',
    'https://*.raisely.com',
    'https://*.fundraiseup.com',
    'https://*.paypal.com',
    'https://*.paypalobjects.com',
    'https://*.autoaddress.ie',
    'https://maxcdn.bootstrapcdn.com',
  ],
};

/**
 * Datadog ddsource label for CSP violation reports.
 * Keeps suite / stage / branch noise out of production monitors.
 *
 * @param {string} countryCode
 * @param {string} userAgent
 * @param {string} environment - NEXT_PUBLIC_ENVIRONMENT
 * @param {string} frontendHost - FRONTEND_CNET_URL (or similar)
 * @returns {string}
 */
export const buildCspDdSource = (
  countryCode = 'IE',
  userAgent = '',
  environment = 'development',
  frontendHost = '',
) => {
  const isTestUa = userAgent.includes('CircleCI-Codeception-Tests');
  const isStage = frontendHost.includes('stage.');
  // GB-v2 replaces former UK-v1 so the countryCode change is obvious in Datadog.
  const prodVersion = countryCode === 'GB' ? 'v2' : 'v5';

  if (isTestUa) {
    return `csp-report-${countryCode}-tests`;
  }
  if (environment === 'production') {
    return `csp-report-${countryCode}-${prodVersion}`;
  }
  if (isStage) {
    return `csp-report-${countryCode}-stage`;
  }
  return `csp-report-${countryCode}-dev`;
};

/**
 * Builds the Content Security Policy string
 * @param {string} nonce - The nonce value to include in the CSP
 * @param {string} cspCentralEndpointId - The CSP Central Endpoint ID for reporting
 * @param {string} userAgent - The user agent string to check for special handling
 * @param {string} countryCode - The country code to use for reporting
 * @param {string} environment - NEXT_PUBLIC_ENVIRONMENT (development | production)
 * @param {string} frontendHost - Frontend origin used to detect stage hosts
 * @returns {string} The complete CSP string
 */
export const buildCSPString = (
  nonce,
  cspCentralEndpointId,
  userAgent = '',
  countryCode = 'IE',
  environment = 'development',
  frontendHost = '',
) => {
  // In-app / legacy browsers that need eval. CircleCI tests intentionally
  // excluded so the suite matches normal Chrome (no unsafe-eval).
  const needsUnsafeEval =
    userAgent.includes('musical_ly') || // TikTok
    userAgent.includes('Instagram') || // Instagram
    userAgent.includes('FBAN') || // Facebook
    userAgent.includes('FBAV') || // Facebook
    /Chrome\/[67]\d\./.test(userAgent); // Very old Chrome (60-79)

  const unsafeEval = needsUnsafeEval ? " 'unsafe-eval'" : '';
  const ddsource = buildCspDdSource(countryCode, userAgent, environment, frontendHost);
  const directives = [
    `base-uri 'self'`,
    `default-src 'self' ${CSP_DOMAINS.defaultSrc.join(' ')} 'nonce-${nonce}' 'strict-dynamic'${unsafeEval} https:`,
    // Keep permissive during investigation.
    `connect-src 'self' https: wss:`,
    `script-src-attr 'unsafe-inline'`,
    `style-src 'self' 'unsafe-inline' https:`,
    `img-src 'self' https: data: blob:`,
    `font-src 'self' https: data:`,
    `object-src 'none'`,
    `frame-ancestors 'self'`,
    `upgrade-insecure-requests`,
    `block-all-mixed-content`,
    `form-action 'self' https://*.facebook.com`,
    // Allow Instagram and other social media widgets
    // to embed content for now (permissive).
    `frame-src 'self' https:`,
    `report-uri https://browser-intake-datadoghq.eu/api/v2/logs?dd-api-key=${cspCentralEndpointId}&dd-evp-origin=content-security-policy&ddsource=${ddsource}`,
  ];

  return directives.join('; ');
};
